import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ChevronRight, FileText } from "lucide-react";
import { orderApi } from "../../api/cart";
import { OrderStatusBadge } from "../../components/cart/OrderStatusBadge";
import { EmptyState, LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Icon } from "../../components/ui";
import { formatDay } from "../../lib/dates";
import { FULFILMENT_LABEL } from "../../lib/fulfilment";
import { formatPesos } from "../../lib/money";
import { usePageTitle } from "../catalogue/usePageTitle";
import { SignedInOnly } from "./SignedInOnly";

export const ORDERS_KEY = ["account", "orders"] as const;

/** PRD screen 11: every order, newest first, with where it is. */
export function OrdersRoute() {
  usePageTitle("Your orders");
  return (
    <SignedInOnly>
      <Orders />
    </SignedInOnly>
  );
}

function Orders() {
  const orders = useQuery({ queryKey: ORDERS_KEY, queryFn: orderApi.list });
  return (
    <PageWidth className="max-w-[860px] pt-8">
      <h1 className="mb-6 type-h1 [font-size:var(--text-2xl)]">Your orders</h1>
      {orders.isPending ? (
        <p className="type-body text-text-muted">Loading your orders…</p>
      ) : orders.isError ? (
        <LoadError onRetry={() => orders.refetch()} />
      ) : orders.data.length === 0 ? (
        <EmptyState icon={FileText} title="No orders yet" action={<Link to="/equipment-categories">Browse machines</Link>}>
          When you place an order, it shows here with where it is.
        </EmptyState>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {orders.data.map((order) => (
            <li key={order.reference}>
              <Link
                to={`/orders/${order.reference}`}
                className="flex min-h-16 items-center gap-4 rounded-md border border-border-subtle bg-surface-card p-4 hover:border-green-700"
              >
                <span className="min-w-0 flex-1">
                  <span className="block type-sku [font-size:var(--text-sm)] text-text-strong">{order.reference}</span>
                  <span className="mt-0.5 block type-small text-text-muted">
                    {formatDay(order.placed_at)} · {order.item_count} {order.item_count === 1 ? "item" : "items"} · {FULFILMENT_LABEL[order.fulfilment]}
                  </span>
                </span>
                <span className="flex flex-none flex-col items-end gap-1.5">
                  <OrderStatusBadge order={order} />
                  <span className="type-price [font-size:var(--text-base)] text-text-strong">{formatPesos(order.total_cents)}</span>
                </span>
                <Icon icon={ChevronRight} size={18} className="flex-none text-ink-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PageWidth>
  );
}
